import React from 'react'
import Transition from 'react-motion-ui-pack'
import Portal from 'react-portal'

import { ButtonStyle, StyledButton } from '@acorns/web-components'
import styled from 'styled-components'

import themeObj from 'theme'

import { TestIDs } from '../../test/utils/testIds'

type Props = {
  children: any
  isOpen: boolean
  onCloseClick?: () => void
  title?: string
}

type InnerProps = {
  allowPointerEvents: boolean
}

export type AgreementModalProps = Props

const Transitions = {
  Overlay: {
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  },

  Modal: {
    enter: {
      opacity: 1,
      scale: 1,
      translateY: 0,
    },
    leave: {
      opacity: 0,
    },
  },
}

const Container = styled.div<InnerProps>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100vw;
  height: 100vh;
  z-index: 99;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-y: hidden;
  pointer-events: ${({ allowPointerEvents }) => (allowPointerEvents ? 'all' : 'none')};
`

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: -1;
`

const Modal = styled.div`
  width: calc(100% - 20px);
  max-width: 700px;
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
  z-index: 9;
  transition: height 300ms ease;
  height: 600px;
  max-height: calc(90vh);
  display: flex;
  flex-direction: column;

  @media screen and (max-width: ${themeObj.breakpoints.sm}px) {
    max-height: calc(80vh);
  }
`

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  border-top: 1px solid ${themeObj.colors.ivory};
`

const CopyFadeOut = styled.div`
  background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1));
  height: 50px;
  margin-top: -45px;
  padding: 0;
  width: calc(100% - 15px);
  z-index: 26;

  @media screen and (max-width: ${themeObj.breakpoints.sm}px) {
    margin-top: -45px;
  }
`

const AgreementModal: React.SFC<Props> = ({ title, isOpen, onCloseClick, ...props }) => (
  <Portal isOpened={isOpen}>
    <Container allowPointerEvents={isOpen} data-testid={TestIDs.AGREEMENT_MODAL.CONTAINER}>
      <Transition enter={Transitions.Overlay.enter} leave={Transitions.Overlay.leave}>
        {isOpen && (
          <Overlay
            key={'overlay'}
            onClick={onCloseClick}
            data-testid={TestIDs.AGREEMENT_MODAL.OVERLAY}
          />
        )}
      </Transition>

      <Transition
        component={false}
        measure={false}
        enter={Transitions.Modal.enter}
        leave={Transitions.Modal.leave}
      >
        {isOpen && (
          <Modal key={'modal'}>
            {props.children}

            <CopyFadeOut />
            <ButtonContainer>
              <StyledButton
                buttonStyle={ButtonStyle.row}
                data-testid={TestIDs.AGREEMENT_MODAL.CLOSE_BUTTON}
                onPress={onCloseClick}
                styleType={'flat'}
                theme={themeObj}
              >
                Ok, Got it
              </StyledButton>
            </ButtonContainer>
          </Modal>
        )}
      </Transition>
    </Container>
  </Portal>
)

export default AgreementModal
